import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, formatErr } from "@/lib/api";

const IntakeFields = ({ form, set, setForm }) => (
  <div className="space-y-5 pt-2 border-t border-[#dacac1]" data-testid="ambassador-intake">
    <div><label className="label">How did you hear about us?</label><input required className="field" value={form.heard_from} onChange={set("heard_from")} data-testid="intake-heard-input" /></div>
    <div><label className="label">What interests you about the healers community?</label><textarea required rows={4} className="field" value={form.interest} onChange={set("interest")} data-testid="intake-interest-input" /></div>
    <div>
      <label className="label">On a scale from 1-10, how ready are you to be a part of a healing community?</label>
      <div className="flex flex-wrap gap-2 mt-2">
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
          <button type="button" key={n} onClick={() => setForm({ ...form, readiness: String(n) })} data-testid={`intake-readiness-${n}`}
            className={`h-10 w-10 rounded-full border text-sm transition-colors duration-200 ${form.readiness === String(n) ? "bg-[#b6689a] border-[#b6689a] text-white" : "border-[#dacac1] hover:border-[#b6689a]"}`}>{n}</button>
        ))}
      </div>
      <input tabIndex={-1} aria-hidden="true" className="sr-only" value={form.readiness} readOnly />
    </div>
    <div><label className="label">What is the number 1 thing you would like to heal, learn, or remember here?</label><textarea required rows={3} className="field" value={form.focus} onChange={set("focus")} data-testid="intake-focus-input" /></div>
  </div>
);

export const AuthCard = ({ mode, setMode, next }) => {
  const { login, register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "", heard_from: "", interest: "", readiness: "", focus: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [held, setHeld] = useState(null);
  const [forgot, setForgot] = useState(false);
  const [sent, setSent] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      if (mode === "signup") {
        if (!form.readiness) {
          setError("Please choose how ready you are, from 1 to 10.");
          return;
        }
        setHeld(await register({ ...form, readiness: Number(form.readiness) }));
        return;
      }
      const u = await login(form.email, form.password);
      toast.success(`Welcome home, ${u.name.split(" ")[0]}.`);
      nav(next || (u.role === "admin" ? "/admin" : "/members"));
    } catch (err) {
      setError(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  const tab = (m, label) => (
    <button type="button" onClick={() => setMode(m)} data-testid={`auth-tab-${m}`}
      className={`flex-1 rounded-full py-2.5 text-sm transition-colors duration-300 ${mode === m ? "bg-[#577731] text-white" : "text-[#232b1b]/70 hover:bg-[#b1cc90]/30"}`}>
      {label}
    </button>
  );

  const requestReset = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email: form.email });
      setSent(data.message);
    } catch (err) {
      setError(formatErr(err));
    } finally {
      setBusy(false);
    }
  };

  if (forgot) {
    return (
      <form onSubmit={requestReset} data-testid="forgot-form" className="card-soft p-8 sm:p-10 bg-white/85">
        <p className="eyebrow">Forgot your password?</p>
        <h3 className="font-serif-h text-3xl text-[#577731] mt-4">We will light the way back.</h3>
        {sent ? (
          <p data-testid="forgot-sent" className="mt-6 leading-relaxed text-[#232b1b]/80">{sent}</p>
        ) : (
          <div className="mt-6 space-y-5">
            <p className="text-sm text-[#232b1b]/70">Share the email you joined with, and we will send you a link to choose a new password.</p>
            <div><label className="label">Email</label><input required type="email" className="field" value={form.email} onChange={set("email")} data-testid="forgot-email-input" /></div>
            {error && <p data-testid="forgot-error" className="text-sm text-[#9c3d5c]">{error}</p>}
            <button disabled={busy} data-testid="forgot-submit-btn" className="btn btn-olive w-full">{busy ? "One breath…" : "Send My Reset Link"}</button>
          </div>
        )}
        <button type="button" onClick={() => { setForgot(false); setSent(""); setError(""); }} data-testid="forgot-back-btn" className="mt-6 text-sm underline underline-offset-4 text-[#577731]">Back to Ambassador Login</button>
      </form>
    );
  }

  if (held) {
    return (
      <div data-testid="auth-pending" className="card-soft p-8 sm:p-10 bg-white/85 text-center">
        <p className="eyebrow">Your request is held</p>
        <h3 className="font-serif-h text-3xl text-[#577731] mt-4">Thank you, {held.name.split(" ")[0]}.</h3>
        <p className="mt-5 leading-relaxed text-[#232b1b]/80">{held.message}</p>
        <button type="button" onClick={() => { setHeld(null); setMode("login"); }} className="btn btn-ghost mt-8" data-testid="auth-pending-login-btn">Back to Ambassador Login</button>
      </div>
    );
  }

  return (
    <form onSubmit={submit} data-testid="auth-form" className="card-soft p-8 sm:p-10 bg-white/85">
      <div className="flex gap-2 p-1 rounded-full bg-[#dacac1]/40">
        {tab("signup", "Apply to Become an Ambassador")}
        {tab("login", "Ambassador Login")}
      </div>
      <div className="mt-8 space-y-5">
        {mode === "signup" && (
          <>
            <div><label className="label">Full name</label><input required className="field" value={form.name} onChange={set("name")} data-testid="auth-name-input" /></div>
            <div><label className="label">Phone</label><input className="field" value={form.phone} onChange={set("phone")} data-testid="auth-phone-input" /></div>
          </>
        )}
        <div><label className="label">Email</label><input required type="email" className="field" value={form.email} onChange={set("email")} data-testid="auth-email-input" /></div>
        <div><label className="label">Password</label><input required type="password" minLength={6} className="field" value={form.password} onChange={set("password")} data-testid="auth-password-input" /></div>
        {mode === "signup" && <IntakeFields form={form} set={set} setForm={setForm} />}
        {mode === "login" && (
          <button type="button" onClick={() => { setForgot(true); setError(""); }} data-testid="auth-forgot-link" className="-mt-2 text-sm underline underline-offset-4 text-[#577731] hover:text-[#b6689a] transition-colors">Forgot your password?</button>
        )}
        {error && <p data-testid="auth-error" className="text-sm text-[#9c3d5c]">{error}</p>}
        <button disabled={busy} data-testid="auth-submit-btn" className="btn btn-olive w-full">
          {busy ? "One breath…" : mode === "login" ? "Enter the Circle" : "Apply to Become a Healing Ambassador"}
        </button>
      </div>
    </form>
  );
};
